import { VTOStorage } from '../../utils/vtoStorage';
import { loadHistoryFromStorage } from '../slices/vtoSlice';

/**
 * Enhanced middleware to persist VTO state with improved coordination and error handling
 */
export const persistVTOState = (store) => (next) => (action) => {
  const result = next(action);
  
  // Enhanced VTO persistence triggers with better coordination
  if (action.type === 'vto/generationSuccess' || 
      action.type === 'vto/clearVTOSession' ||
      action.type === 'personImage/setVirtualTryOnResult' ||
      action.type === 'clothingItems/setActiveItems') {
    
    console.log('[VTO] Persistence middleware triggered by action:', action.type);
    
    const state = store.getState();
    
    try {
      // Coordinate VTO and person image state with enhanced wearer/wearing persistence (URLs only)
      const vtoState = {
        history: state.vto.history,
        originalPersonImageUrl: state.vto.originalPersonImageUrl || state.personImage?.image, // WEARER image URL
        originalPersonImageId: state.vto.originalPersonImageId || state.personImage?.activePersonImageId, // WEARER image backend ID
        activeClothingItems: state.clothingItems?.activeItems || [], // WEARING items
        selectedForTryOn: state.clothingItems?.selectedForTryOn || [], // Selected item IDs
        currentGeneration: state.vto.currentGeneration,
        lastActivity: Date.now(),
        // Enhanced wearer/wearing context
        wearerContext: {
          originalImageUrl: state.vto.originalPersonImageUrl || state.personImage?.image,
          originalImageId: state.vto.originalPersonImageId || state.personImage?.activePersonImageId,
          currentPersonImageUrl: state.personImage?.image,
          isVTOResult: state.personImage?.isVirtualTryOnResult || false,
          position: state.personImage?.position || { x: 0, y: 0 },
          scale: state.personImage?.scale || 1.0
        },
        wearingContext: {
          activeItems: state.clothingItems?.activeItems || [],
          selectedIds: state.clothingItems?.selectedForTryOn || [],
          itemCount: (state.clothingItems?.activeItems || []).length
        }
      };
      
      // Enhanced persistence with error handling
      const enhancedSaveResult = VTOStorage.saveEnhancedState(vtoState);
      
      // Also save to original history for backward compatibility (only if enhanced save succeeded)
      if (enhancedSaveResult) {
        VTOStorage.saveHistory(state.vto.history);
      } else {
        console.warn('[VTO] Enhanced VTO state save failed, skipping history save to prevent quota issues');
      }
      
      // Ensure person image state includes VTO context when it's a VTO result (URLs only)
      if (state.personImage.isVirtualTryOnResult && state.personImage.image) {
        VTOStorage.saveVTOContext({
          imageUrl: state.personImage.image, // URL only
          imageId: state.personImage.activePersonImageId, // Backend ID
          metadata: state.personImage.virtualTryOnMetadata,
          position: state.personImage.position,
          scale: state.personImage.scale,
          clothingItems: state.clothingItems?.activeItems || [],
          timestamp: Date.now(),
          // Enhanced wearer/wearing context
          wearerImageUrl: state.vto.originalPersonImageUrl || state.personImage?.image,
          wearingItems: state.clothingItems?.activeItems || [],
          wearingItemIds: state.clothingItems?.selectedForTryOn || []
        });
      }
      
      // Save original person image URL and ID with enhanced error handling
      if (state.vto.originalPersonImageUrl || state.vto.originalPersonImageId) {
        try {
          const originalPersonData = {
            url: state.vto.originalPersonImageUrl,
            id: state.vto.originalPersonImageId,
            timestamp: Date.now()
          };
          localStorage.setItem('vto_original_person_image', JSON.stringify(originalPersonData));
        } catch (error) {
          console.warn('Failed to save original person image data:', error);
          // For URL-based storage, this should rarely fail, but handle gracefully
          if (error.name === 'QuotaExceededError') {
            // Clear other data and try again with minimal data
            VTOStorage.clearLargeStorageItems();
            try {
              localStorage.setItem('vto_original_person_image', JSON.stringify({
                url: state.vto.originalPersonImageUrl,
                id: state.vto.originalPersonImageId
              }));
            } catch (retryError) {
              console.warn('Failed to save minimal original person image data:', retryError);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Enhanced VTO persistence failed:', error);
      // Fallback to basic persistence
      try {
        VTOStorage.saveHistory(state.vto.history);
      } catch (fallbackError) {
        console.error('Fallback VTO persistence also failed:', fallbackError);
      }
    }
  }
  
  return result;
};

/**
 * Enhanced VTO state restoration with improved coordination and error handling
 */
export const loadVTOHistoryOnStart = (store) => {
  try {
    // Load enhanced VTO state
    const enhancedState = VTOStorage.loadEnhancedState();
    
    if (enhancedState && enhancedState.history && enhancedState.history.length > 0) {
      console.log('[VTO] Loading enhanced VTO state from localStorage:', enhancedState.history.length, 'items');
      
      // Restore VTO history
      store.dispatch(loadHistoryFromStorage(enhancedState.history));
      
      // Restore original person image (URL and ID)
      if (enhancedState.originalPersonImageUrl || enhancedState.originalPersonImageId) {
        console.log('[VTO] Restoring original person image (URL-based)');
        store.dispatch({
          type: 'vto/setOriginalPersonImage',
          payload: {
            url: enhancedState.originalPersonImageUrl,
            id: enhancedState.originalPersonImageId
          }
        });
      }
      
      // Restore active clothing items first (WEARING section)
      if (enhancedState.activeClothingItems && enhancedState.activeClothingItems.length > 0) {
        console.log('[VTO] Restoring WEARING items (active clothing):', enhancedState.activeClothingItems.length);
        store.dispatch({
          type: 'clothingItems/setActiveItems',
          payload: enhancedState.activeClothingItems
        });
        
        // Also restore the selected IDs if available
        if (enhancedState.selectedForTryOn && enhancedState.selectedForTryOn.length > 0) {
          console.log('[VTO] Restoring selected item IDs:', enhancedState.selectedForTryOn.length);
          // The setActiveItems action should handle this, but we can ensure consistency
        }
      }
      
      // Restore the most recent VTO result to canvas with enhanced context
      const mostRecent = enhancedState.currentGeneration || enhancedState.history[0];
      if (mostRecent && mostRecent.imageUrl) {
        console.log('[VTO] Restoring most recent VTO result to canvas with enhanced context');
        
        // Load VTO context if available
        const vtoContext = VTOStorage.loadVTOContext();
        
        store.dispatch({
          type: 'personImage/setVirtualTryOnResult',
          payload: {
            image: mostRecent.imageUrl,
            metadata: {
              isVirtualTryOnResult: true,
              timestamp: mostRecent.timestamp,
              clothingItems: mostRecent.clothingItems || enhancedState.activeClothingItems,
              sessionId: vtoContext?.sessionId,
              generationMetadata: mostRecent.metadata
            }
          }
        });
        
        // Restore position and scale if available from VTO context
        if (vtoContext && (vtoContext.position || vtoContext.scale)) {
          if (vtoContext.position) {
            store.dispatch({
              type: 'personImage/updatePosition',
              payload: vtoContext.position
            });
          }
          if (vtoContext.scale) {
            store.dispatch({
              type: 'personImage/updateScale',
              payload: vtoContext.scale
            });
          }
        }
        
        // Restore enhanced wearer/wearing context if available
        if (vtoContext && vtoContext.wearingItems && vtoContext.wearingItems.length > 0) {
          console.log('[VTO] Restoring WEARING items from VTO context:', vtoContext.wearingItems.length);
          store.dispatch({
            type: 'clothingItems/setActiveItems',
            payload: vtoContext.wearingItems
          });
        }
        
        if (vtoContext && vtoContext.wearerImageUrl && !enhancedState.originalPersonImageUrl) {
          console.log('[VTO] Restoring WEARER image from VTO context (URL-based)');
          store.dispatch({
            type: 'vto/setOriginalPersonImage',
            payload: {
              url: vtoContext.wearerImageUrl,
              id: vtoContext.imageId
            }
          });
        }
      }
      
      return true;
    } else {
      // Fallback to legacy loading method
      console.log('[VTO] Falling back to legacy VTO history loading');
      return loadVTOHistoryLegacy(store);
    }
  } catch (error) {
    console.error('Enhanced VTO state loading failed:', error);
    // Fallback to legacy loading method
    return loadVTOHistoryLegacy(store);
  }
};

/**
 * Legacy VTO history loading (fallback)
 */
const loadVTOHistoryLegacy = (store) => {
  try {
    const history = VTOStorage.loadHistory();
    if (history.length > 0) {
      console.log('[VTO] Loading VTO history from localStorage (legacy):', history.length, 'items');
      store.dispatch(loadHistoryFromStorage(history));
      
      // Load the original person image if it exists
      try {
        const originalPersonImageData = localStorage.getItem('vto_original_person_image');
        if (originalPersonImageData) {
          console.log('[VTO] Restoring original person image (legacy)');
          
          // Try to parse as JSON (new format), fallback to string (old format)
          try {
            const parsedData = JSON.parse(originalPersonImageData);
            store.dispatch({
              type: 'vto/setOriginalPersonImage',
              payload: {
                url: parsedData.url,
                id: parsedData.id
              }
            });
          } catch (parseError) {
            // Old format - treat as direct URL/base64
            store.dispatch({
              type: 'vto/setOriginalPersonImage',
              payload: {
                url: originalPersonImageData,
                id: null
              }
            });
          }
        }
      } catch (error) {
        console.warn('Failed to load original person image:', error);
      }
      
      // Also update the person image with the most recent VTO result
      const mostRecent = history[0];
      if (mostRecent && mostRecent.imageUrl) {
        console.log('[VTO] Restoring most recent VTO result to canvas (legacy)');
        
        // Restore the person image with VTO result
        store.dispatch({
          type: 'personImage/setVirtualTryOnResult',
          payload: {
            image: mostRecent.imageUrl,
            metadata: {
              isVirtualTryOnResult: true,
              timestamp: mostRecent.timestamp,
              clothingItems: mostRecent.clothingItems
            }
          }
        });
        
        // Restore the active clothing items
        if (mostRecent.clothingItems && mostRecent.clothingItems.length > 0) {
          console.log('[VTO] Restoring active clothing items (legacy):', mostRecent.clothingItems.length);
          store.dispatch({
            type: 'clothingItems/setActiveItems',
            payload: mostRecent.clothingItems
          });
        }
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('Legacy VTO history loading failed:', error);
    return false;
  }
};

export default persistVTOState;